import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Phone, Mail, Trash2, Loader2, Lock, Search, Save, Inbox, ArrowLeft,
  Star, MessageSquareText, Check, EyeOff, Images, Upload, Plus, Globe,
} from "lucide-react";
import { Link } from "react-router-dom";
import { LOGO } from "@/data";
import { useLang } from "@/i18n";
import { compressImage } from "@/lib/image";
import {
  login, fetchMe, fetchEstimates, updateEstimate, deleteEstimate,
  fetchAllReviews, updateReview, deleteReview,
  fetchGallery, addGalleryImage, deleteGalleryImage,
  getToken, clearToken, formatApiError,
} from "@/lib/adminApi";

const STATUSES = ["New", "Contacted", "Done"];
const STATUS_STYLES = {
  New: "bg-[#d4af37] text-black",
  Contacted: "bg-blue-500/20 text-blue-300 border border-blue-400/40",
  Done: "bg-green-500/20 text-green-300 border border-green-400/40",
};

/* ---------------- Login ---------------- */
function LoginScreen({ onSuccess }) {
  const { t } = useLang();
  const a = t.admin;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      toast.success(a.welcome);
      onSuccess(user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || a.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#d4af37]/25 px-4 py-3.5 text-white placeholder-white/35 focus:border-[#d4af37] focus:outline-none focus:ring-1 focus:ring-[#d4af37]/40 transition-colors duration-300";

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md border border-[#d4af37]/20 p-9 bg-[#080808]"
      >
        <img src={LOGO} alt="Banman's Painting" className="h-16 w-16 rounded-full object-cover ring-1 ring-[#d4af37]/40 mx-auto" />
        <h1 className="mt-6 text-center font-serif-lux font-light text-3xl text-white">{a.loginTitle}</h1>
        <p className="mt-2 text-center text-white/50 text-sm flex items-center justify-center gap-2">
          <Lock size={13} /> {a.loginSubtitle}
        </p>
        <div className="mt-8 space-y-4">
          <input data-testid="login-email" type="email" className={inputCls} placeholder={a.email} value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="login-password" type="password" className={inputCls} placeholder={a.password} value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#d4af37] text-black px-6 py-3.5 uppercase tracking-[0.15em] text-sm font-medium gold-glow disabled:opacity-60 hover:bg-[#f1e5ac] transition-colors duration-300"
        >
          {loading ? <><Loader2 size={16} className="animate-spin" /> {a.signingIn}</> : a.signIn}
        </button>
        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-white/40 hover:text-[#d4af37] text-sm transition-colors duration-300">
          <ArrowLeft size={14} /> {a.backToSite}
        </Link>
      </motion.form>
    </div>
  );
}

/* ---------------- Estimate card ---------------- */
function EstimateCard({ est, onUpdate, onDelete }) {
  const { t, lang } = useLang();
  const a = t.admin.est;
  const [notes, setNotes] = useState(est.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const dirty = notes !== (est.notes || "");

  const changeStatus = async (status) => {
    try {
      const updated = await updateEstimate(est.id, { status });
      onUpdate(updated);
      toast.success(`${a.markedAs} ${a.statusLabels[status] || status}`);
    } catch {
      toast.error(a.errStatus);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const updated = await updateEstimate(est.id, { notes });
      onUpdate(updated);
      toast.success(a.notesSaved);
    } catch {
      toast.error(a.errNotes);
    } finally {
      setSavingNotes(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`${a.deleteConfirm} ${est.name}?`)) return;
    try {
      await deleteEstimate(est.id);
      onDelete(est.id);
      toast.success(a.deleted);
    } catch {
      toast.error(a.errDelete);
    }
  };

  const date = new Date(est.created_at).toLocaleString(lang === "es" ? "es-ES" : "en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35 }}
      className="border border-[#d4af37]/20 bg-[#080808] p-6"
      data-testid={`estimate-card-${est.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif-lux text-2xl text-white">{est.name}</h3>
          <p className="text-white/40 text-xs mt-1">{date}</p>
        </div>
        <span className={`text-[11px] uppercase tracking-[0.12em] px-3 py-1 ${STATUS_STYLES[est.status] || STATUS_STYLES.New}`}>
          {a.statusLabels[est.status] || est.status}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        <a href={`tel:${est.phone}`} data-testid={`call-${est.id}`} className="inline-flex items-center gap-2 text-[#d4af37] hover:text-[#f1e5ac] transition-colors duration-300">
          <Phone size={14} /> {est.phone}
        </a>
        {est.email && (
          <a href={`mailto:${est.email}`} className="inline-flex items-center gap-2 text-white/70 hover:text-[#d4af37] transition-colors duration-300">
            <Mail size={14} /> {est.email}
          </a>
        )}
      </div>

      <div className="mt-3">
        <span className="text-[11px] uppercase tracking-[0.15em] text-white/40">{a.service}</span>
        <p className="text-white/85">{est.service_type}</p>
      </div>

      {est.message && (
        <div className="mt-3">
          <span className="text-[11px] uppercase tracking-[0.15em] text-white/40">{a.message}</span>
          <p className="text-white/70 font-light">{est.message}</p>
        </div>
      )}

      {/* Status buttons */}
      <div className="mt-5 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => changeStatus(s)}
            data-testid={`status-${s.toLowerCase()}-${est.id}`}
            className={`text-xs uppercase tracking-[0.1em] px-3 py-1.5 border transition-colors duration-300 ${
              est.status === s ? "border-[#d4af37] text-[#d4af37]" : "border-white/15 text-white/50 hover:border-[#d4af37]/50 hover:text-white"
            }`}
          >
            {a.statusLabels[s]}
          </button>
        ))}
      </div>

      {/* Notes */}
      <div className="mt-5">
        <span className="text-[11px] uppercase tracking-[0.15em] text-white/40">{a.notes}</span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid={`notes-${est.id}`}
          placeholder={a.notesPh}
          className="mt-2 w-full bg-transparent border border-[#d4af37]/20 px-3 py-2 text-white/85 text-sm placeholder-white/30 focus:border-[#d4af37] focus:outline-none resize-none transition-colors duration-300"
        />
        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={saveNotes}
            disabled={!dirty || savingNotes}
            data-testid={`save-notes-${est.id}`}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[#d4af37] disabled:text-white/25 transition-colors duration-300"
          >
            {savingNotes ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {a.saveNotes}
          </button>
          <button
            onClick={remove}
            data-testid={`delete-${est.id}`}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-red-400/70 hover:text-red-400 transition-colors duration-300"
          >
            <Trash2 size={13} /> {a.del}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ---------------- Dashboard ---------------- */
function GalleryPanel() {
  const { t } = useLang();
  const g = t.admin.gal;
  const [category, setCategory] = useState("interior");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async (cat) => {
    setLoading(true);
    try {
      setImages(await fetchGallery(cat));
    } catch {
      toast.error(g.errLoad);
    } finally {
      setLoading(false);
    }
  }, [g.errLoad]);

  useEffect(() => { load(category); }, [load, category]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const compressed = await compressImage(file);
        const created = await addGalleryImage(category, compressed);
        setImages((list) => [...list, created]);
      }
      toast.success(files.length > 1 ? `${files.length} ${g.addedMany}` : g.addedOne);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : g.uploadFailed);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (img) => {
    if (!window.confirm(g.removeConfirm)) return;
    try {
      await deleteGalleryImage(img.id);
      setImages((list) => list.filter((x) => x.id !== img.id));
      toast.success(g.removed);
    } catch {
      toast.error(g.errRemove);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex gap-2">
          {["interior", "exterior"].map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`gallery-cat-${c}`}
              className={`capitalize text-xs uppercase tracking-[0.12em] px-5 py-2.5 border transition-colors duration-300 ${category === c ? "border-[#d4af37] text-[#d4af37]" : "border-white/15 text-white/50 hover:text-white"}`}
            >
              {g[c]}
            </button>
          ))}
        </div>
        <label className={`inline-flex items-center gap-2 bg-[#d4af37] text-black px-5 py-2.5 text-xs uppercase tracking-[0.12em] font-medium cursor-pointer hover:bg-[#f1e5ac] transition-colors duration-300 ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="gallery-upload-label">
          {uploading ? <><Loader2 size={15} className="animate-spin" /> {g.uploading}</> : <><Plus size={15} /> {g.addPhotos}</>}
          <input type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="gallery-upload-input" />
        </label>
      </div>

      <p className="text-white/40 text-sm mb-6">{images.length} {images.length === 1 ? g.infoA : g.infoB} {g[category]}{g.infoC}</p>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/40"><Loader2 className="animate-spin mr-2" /> {g.loading}</div>
      ) : images.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-white/40" data-testid="gallery-empty">
          <Images size={40} className="mb-4 text-[#d4af37]/60" />
          <p>{g.empty}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, i) => (
            <div key={img.id} className="group relative border border-[#d4af37]/20 overflow-hidden aspect-square" data-testid={`gallery-img-${img.id}`}>
              <img src={img.image} alt={`${category} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-2 left-2 bg-[#d4af37] text-black text-[10px] uppercase tracking-[0.1em] px-2 py-0.5">{g.cover}</span>
              )}
              <button
                onClick={() => remove(img)}
                data-testid={`gallery-delete-${img.id}`}
                className="absolute inset-0 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-red-300 hover:text-red-400"
                aria-label="Remove photo"
              >
                <Trash2 size={22} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewsPanel() {
  const { t } = useLang();
  const rv = t.admin.rev;
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReviews(await fetchAllReviews());
    } catch {
      toast.error(rv.errLoad);
    } finally {
      setLoading(false);
    }
  }, [rv.errLoad]);

  useEffect(() => { load(); }, [load]);

  const setApproved = async (r, approved) => {
    try {
      const updated = await updateReview(r.id, { approved });
      setReviews((list) => list.map((x) => (x.id === r.id ? updated : x)));
      toast.success(approved ? rv.publishedToast : rv.hiddenToast);
    } catch {
      toast.error(rv.errUpdate);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`${rv.deleteConfirm} ${r.name}?`)) return;
    try {
      await deleteReview(r.id);
      setReviews((list) => list.filter((x) => x.id !== r.id));
      toast.success(rv.deleted);
    } catch {
      toast.error(rv.errDelete);
    }
  };

  const pending = reviews.filter((r) => !r.approved);
  const published = reviews.filter((r) => r.approved);
  const shown = filter === "Pending" ? pending : filter === "Published" ? published : reviews;
  const filterLabels = { All: t.admin.all, Pending: rv.pending, Published: rv.published };

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-8">
        {[{ label: rv.total, val: reviews.length }, { label: rv.pending, val: pending.length }, { label: rv.published, val: published.length }].map((c) => (
          <div key={c.label} className="border border-[#d4af37]/20 bg-[#080808] p-5">
            <p className="text-4xl font-serif-lux text-[#d4af37]">{c.val}</p>
            <p className="text-white/50 text-xs uppercase tracking-[0.15em] mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {["All", "Pending", "Published"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`review-filter-${s.toLowerCase()}`}
            className={`text-xs uppercase tracking-[0.12em] px-4 py-2.5 border transition-colors duration-300 ${filter === s ? "border-[#d4af37] text-[#d4af37]" : "border-white/15 text-white/50 hover:text-white"}`}
          >
            {filterLabels[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/40"><Loader2 className="animate-spin mr-2" /> {rv.loading}</div>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-white/40" data-testid="reviews-empty">
          <MessageSquareText size={40} className="mb-4 text-[#d4af37]/60" />
          <p>{rv.empty}{filter !== "All" ? ` — ${filterLabels[filter]}` : ""}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {shown.map((r) => (
            <div key={r.id} className="border border-[#d4af37]/20 bg-[#080808] p-6" data-testid={`review-card-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} size={14} className={s < r.rating ? "text-[#d4af37]" : "text-white/15"} fill={s < r.rating ? "#d4af37" : "transparent"} />
                    ))}
                  </div>
                  <h3 className="font-serif-lux text-2xl text-white">{r.name}</h3>
                  {r.location && <p className="text-white/40 text-xs">{r.location}</p>}
                </div>
                <span className={`text-[11px] uppercase tracking-[0.12em] px-3 py-1 ${r.approved ? "bg-green-500/20 text-green-300 border border-green-400/40" : "bg-[#d4af37] text-black"}`}>
                  {r.approved ? rv.published : rv.pending}
                </span>
              </div>
              {r.photo && <img src={r.photo} alt="project" className="w-full h-48 object-cover mt-4 border border-[#d4af37]/15" />}
              <p className="mt-4 text-white/75 font-light italic">&ldquo;{r.text}&rdquo;</p>
              <div className="mt-5 flex items-center gap-2">
                {r.approved ? (
                  <button onClick={() => setApproved(r, false)} data-testid={`review-hide-${r.id}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] border border-white/15 text-white/60 px-4 py-2 hover:border-[#d4af37]/50 hover:text-white transition-colors duration-300">
                    <EyeOff size={14} /> {rv.hide}
                  </button>
                ) : (
                  <button onClick={() => setApproved(r, true)} data-testid={`review-approve-${r.id}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] bg-[#d4af37] text-black px-4 py-2 hover:bg-[#f1e5ac] transition-colors duration-300">
                    <Check size={14} /> {rv.approve}
                  </button>
                )}
                <button onClick={() => remove(r)} data-testid={`review-delete-${r.id}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-red-400/70 hover:text-red-400 px-3 py-2 transition-colors duration-300">
                  <Trash2 size={14} /> {rv.del}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const { t, lang, setLang } = useLang();
  const a = t.admin;
  const [tab, setTab] = useState("estimates");
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchEstimates();
      setEstimates(data);
    } catch {
      toast.error(a.est.errLoad);
    } finally {
      setLoading(false);
    }
  }, [a.est.errLoad]);

  useEffect(() => { load(); }, [load]);

  const onUpdate = (updated) => setEstimates((list) => list.map((e) => (e.id === updated.id ? updated : e)));
  const onDelete = (id) => setEstimates((list) => list.filter((e) => e.id !== id));

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: estimates.filter((e) => e.status === s).length }), {});
  const statusLabel = (s) => (s === "All" ? a.all : a.est.statusLabels[s] || s);

  const filtered = estimates.filter((e) => {
    const matchStatus = filter === "All" || e.status === filter;
    const q = query.toLowerCase();
    const matchQuery = !q || [e.name, e.phone, e.email, e.service_type, e.message].some((v) => (v || "").toLowerCase().includes(q));
    return matchStatus && matchQuery;
  });

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-[#d4af37]/20 bg-black/60 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="logo" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#d4af37]/40" />
            <div>
              <p className="font-serif-lux text-lg text-white leading-none">{a.dashboard}</p>
              <p className="text-white/40 text-xs">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              data-testid="admin-lang-toggle"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-white/60 hover:text-[#d4af37] transition-colors duration-300"
            >
              <Globe size={14} /> {lang === "es" ? "EN" : "ES"}
            </button>
            <Link to="/" className="hidden sm:inline text-white/50 hover:text-[#d4af37] text-sm transition-colors duration-300">{a.viewSite}</Link>
            <button onClick={onLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 border border-[#d4af37]/40 text-[#d4af37] px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-[#d4af37] hover:text-black transition-colors duration-300">
              <LogOut size={14} /> {a.logout}
            </button>
          </div>
        </div>
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 flex gap-1">
          {[{ id: "estimates", label: a.tabs.estimates, Icon: Inbox }, { id: "reviews", label: a.tabs.reviews, Icon: Star }, { id: "gallery", label: a.tabs.gallery, Icon: Images }].map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`tab-${id}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.15em] border-b-2 transition-colors duration-300 ${tab === id ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-white/50 hover:text-white"}`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </header>

      {tab === "reviews" ? (
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-10">
          <ReviewsPanel />
        </div>
      ) : tab === "gallery" ? (
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-10">
          <GalleryPanel />
        </div>
      ) : (
      <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[{ label: a.est.total, val: estimates.length }, ...STATUSES.map((s) => ({ label: a.est.statusLabels[s], val: counts[s] }))].map((c) => (
            <div key={c.label} className="border border-[#d4af37]/20 bg-[#080808] p-5">
              <p className="text-4xl font-serif-lux text-[#d4af37]">{c.val}</p>
              <p className="text-white/50 text-xs uppercase tracking-[0.15em] mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="search-input"
              placeholder={a.est.searchPh}
              className="w-full bg-transparent border border-[#d4af37]/20 pl-11 pr-4 py-3 text-white placeholder-white/35 focus:border-[#d4af37] focus:outline-none transition-colors duration-300"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {["All", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s.toLowerCase()}`}
                className={`text-xs uppercase tracking-[0.12em] px-4 py-2.5 border transition-colors duration-300 ${
                  filter === s ? "border-[#d4af37] text-[#d4af37]" : "border-white/15 text-white/50 hover:text-white"
                }`}
              >
                {statusLabel(s)}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-white/40">
            <Loader2 className="animate-spin mr-2" /> {a.est.loading}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-white/40" data-testid="empty-state">
            <Inbox size={40} className="mb-4 text-[#d4af37]/60" />
            <p>{filter !== "All" ? `${a.est.emptyFiltered} "${statusLabel(filter)}".` : a.est.emptyYet}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <AnimatePresence>
              {filtered.map((est) => (
                <EstimateCard key={est.id} est={est} onUpdate={onUpdate} onDelete={onDelete} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

/* ---------------- Container ---------------- */
export default function Admin() {
  const { t } = useLang();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const boot = async () => {
      if (!getToken()) { setChecking(false); return; }
      try {
        const me = await fetchMe();
        setUser(me);
      } catch {
        clearToken();
      } finally {
        setChecking(false);
      }
    };
    boot();
  }, []);

  const logout = () => {
    clearToken();
    setUser(null);
    toast.success(t.admin.loggedOut);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/40">
        <Loader2 className="animate-spin" size={26} />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginScreen onSuccess={setUser} />;
}
